 
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
	useEnabledFlexCredentialsQuery,
	useFlexCredentialByTypeQuery,
	useUploadFlexCredentialMutation,
	useSetFlexPrimaryLicenseGroupMutation
} from '../../../../redux/flex-professional-credentials';
import { PRIVATE_PATH } from '../../../../constants';
import Permissions from '../../../../constants/Permissions';
import UploadGenericCredentialModal from '../../../../components/modals/UploadCredentialModal/UploadGenericCredentialModal';
import UploadPLCredentialModal from '../../../../components/modals/UploadCredentialModal/UploadPLCredentialModal';
import ShowCredentialsByFacilityModal from '../../../../components/modals/ShowCredentialsByFacilityModal/ShowCredentialsByFacilityModal';
import CredentialStatusPill from '../../../../components/CredentialStatusPill/CredentialStatusPill';
import uploadCredentialsIcon from '../../../../assets/images/upload-file.svg';
import eyeIcon from '../../../../assets/images/eye-purple.svg';
import TypeHeader from './TypeHeader';
import ProfessionalCredentialsData from './ProfessionalCredentialsData';
import CredentialsActionUploadButton from './CredentialsActionUploadButton';
import IsAuthorizedForManagementCompany from '../../../../components/IsAuthorizedForManagementCompany/IsAuthorizedForManagementCompany';
import FlexCredentialActionColumn from './row-actions/FlexCredentialActionColumn';
import Utils from '../../../../utils/utils';
import { user } from '../../../../redux/user';
import PrimaryLicenseModal from '../../../../components/modals/PrimaryLicenseModal/PrimaryLicenseModal';
import EditPracticeAreaIcon from '../../../../assets/images/purple-edit-pencil.svg';

const withManagementCompanyAndCaregiver = (managementCompanyId, caregiverId, onUploadSuccess) => (Component) => (props) => {
	// eslint-disable-next-line react/jsx-props-no-spreading
	return <Component managementCompanyId={managementCompanyId} caregiverId={caregiverId} onSuccess={onUploadSuccess}{...props} />;
};

const multipleLicenseGroups = (credentials) => new Set((
	(Array.isArray(credentials) &&
		credentials?.find((credential) => credential.credential_type === 'PROFESSIONAL_LICENSE'))?.submitted ?? [])
	.map(subItem => subItem?.license_group_id)
).size > 1

const FlexProfessionalsCredentialList = () => {
	const { caregiverId, managementCompanyId } = useParams();
	const history = useHistory();

	const userData = useSelector(user, shallowEqual) || {};

	const [expandedRows, setExpandedRows] = useState(null);
	const [credentialTypeCode, setCredentialTypeCode] = useState('');
	const [showUploadGenericModal, setShowUploadGenericModal] = useState(false);
	const [showCredentialsByFacilityModal, setShowCredentialsByFacilityModal] = useState(false);
	const [showUploadPL, setShowUploadPL] = useState(false);
	const [showPrimaryLicenseModal, setShowPrimaryLicenseModal] = useState(false);
	const [hasMultipleLicenseGroups, setHasMultipleLicenseGroups] = useState(false);

	const { data: enabledCredentials, isFetching, isLoading, error, isError, refetch } = useEnabledFlexCredentialsQuery(
		{ managementCompanyId, caregiverId },
		{
			refetchOnMountOrArgChange: true
		}
	);

	useEffect(() => {
		setHasMultipleLicenseGroups(multipleLicenseGroups(enabledCredentials));
	}, [enabledCredentials]);

	const notAccessible = useMemo(() => isError && error?.status === 403, [isError, error]);

	const hasNoProfessionalLicense = useMemo(
		() =>
			Utils.isAuthorizedExact(
				Permissions.WORKFORCE_CREDENTIALS_EDIT,
				userData.managementCompanyPermissions[managementCompanyId]
			) &&
			Array.isArray(enabledCredentials) &&
			enabledCredentials?.some(
				(credential) => credential.credential_type === 'PROFESSIONAL_LICENSE' && !credential.submitted.length
			),
		[enabledCredentials, userData]
	);

	const onClosePLUploadModal = useCallback(() => {
		if (hasNoProfessionalLicense) {
			history.push(
				`${PRIVATE_PATH.WORKFORCE_PAGE}/${managementCompanyId}/professionals/${caregiverId}/employee-details`
			);
		} else {
			setShowUploadPL(false);
		}
	}, [hasNoProfessionalLicense]);

	const onUploadSuccess = async () => {
		setShowUploadPL(false);
		try {
			const { data: refreshedCredentials } = await refetch();
			setShowPrimaryLicenseModal(multipleLicenseGroups(refreshedCredentials));
		} catch {
			setShowPrimaryLicenseModal(multipleLicenseGroups(enabledCredentials));
		}
	};

	const onClosePrimaryLicenseModal = () => {
		setShowPrimaryLicenseModal(false);
	};

	const CredentialActionColumn = withManagementCompanyAndCaregiver(
		managementCompanyId,
		caregiverId,
		onUploadSuccess
	)(FlexCredentialActionColumn);

	const statusTemplate = (item) => (
		<div className="status-container">
			{item.submitted.map((submittedSubtype) => (
				<CredentialStatusPill
					status={submittedSubtype.status}
					title={submittedSubtype.subtype_name}
					key={submittedSubtype.subtype_name}
				/>
			))}
		</div>
	);

	const licensesAndSpecialtiesTemplate = (item, columnData) => {
		const { field } = columnData;
		return <span className="licenses-and-specialties">{item[field] ? item[field] : '-'}</span>;
	};

	const rowExpansionTemplate = (rowData) => (
		<ProfessionalCredentialsData
			caregiverId={caregiverId}
			managementCompanyId={managementCompanyId}
			rowData={rowData}
			enabledCredentials={enabledCredentials}
			ActionsColumnComponent={CredentialActionColumn}
		/>
	);

	const handleUpload = (credentialType) => {
		if (credentialType === 'PROFESSIONAL_LICENSE') {
			setExpandedRows({ [credentialType]: true });
			setShowUploadPL(true);
		} else {
			setShowUploadGenericModal(true);
			setCredentialTypeCode(credentialType);
		}
	};

	const actionsTemplate = (item) => (
		<IsAuthorizedForManagementCompany
			exactPermission={Permissions.WORKFORCE_CREDENTIALS_EDIT}
			managementCompanyId={managementCompanyId}
		>
			<CredentialsActionUploadButton
				credentialType={item?.credential_type}
				onClickCallback={() => handleUpload(item?.credential_type)}
			/>
		</IsAuthorizedForManagementCompany>
	);

	const expanderRowClass = (data) => (data.submitted.length ? 'expandable' : 'default-expander');

	const primaryPracticeAreaText = useMemo(() =>
			enabledCredentials?.find((credential) => credential.credential_type === 'PROFESSIONAL_LICENSE')?.primary_license_group,
		[enabledCredentials]
	);

	return (
		<div className="flex-professionals-credentials">
			<div className="toolbar">
				<IsAuthorizedForManagementCompany
					exactPermission={Permissions.WORKFORCE_CREDENTIALS_EDIT}
					managementCompanyId={managementCompanyId}
				>
					<Button
						disabled={notAccessible}
						className="btn upload-button"
						onClick={() => setShowUploadGenericModal(true)}
					>
						<img src={uploadCredentialsIcon} alt="Upload credentials" />
						<div className="button-label">Upload credentials</div>
					</Button>
					{hasMultipleLicenseGroups && <div className="change-practice-area">
						<span className="title">Primary practice area: </span>
						<span
							className="group"><b>{primaryPracticeAreaText}</b></span>
						<Button
							className="btn upload-button btn-light-purple practice-area-button"
							onClick={() => setShowPrimaryLicenseModal(true)}
						>
							<img src={EditPracticeAreaIcon} alt="Change practice area"/>
						</Button>
					</div>}

				</IsAuthorizedForManagementCompany>

				{showCredentialsByFacilityModal && (
					<Button className="btn upload-button" onClick={() => setShowCredentialsByFacilityModal(true)}>
						<img src={eyeIcon} alt="View credentials by facility" />
						<div className="button-label">View credentials by facility</div>
					</Button>
				)}
			</div>
			<DataTable
				value={enabledCredentials}
				scrollHeight="flex"
				sortMode="multiple"
				virtualScrollerOptions={{ itemSize: 65 }}
				tableStyle={{ tableLayout: 'fixed' }}
				size="small"
				stripedRows
				loading={isLoading}
				removableSort
				rowExpansionTemplate={rowExpansionTemplate}
				expandedRows={expandedRows}
				onRowToggle={(row) => setExpandedRows(row.data)}
				dataKey="credential_type"
			>
				<Column expander bodyClassName={expanderRowClass} style={{ width: '1%', textAlign: 'center' }} />
				<Column
					field="name"
					header="Credential"
					style={{ width: '10%', padding: '1.6rem  1.6rem 1.6rem 0rem' }}
					sortable
					body={licensesAndSpecialtiesTemplate}
				/>
				<Column
					field="submitted"
					header={<TypeHeader />}
					style={{ width: '20%', padding: '0.8rem 1.6rem' }}
					sortable
					body={statusTemplate}
				/>
				<Column
					field="actions"
					header="Actions"
					style={{ width: '3%', padding: '0.8rem 1.6rem' }}
					alignFrozen="right"
					body={actionsTemplate}
				/>
			</DataTable>

			{showUploadGenericModal && (
				<UploadGenericCredentialModal
					credentialType={credentialTypeCode}
					caregiverId={caregiverId}
					managementCompanyId={managementCompanyId}
					enabledCredentials={enabledCredentials}
					uploadCredentialMutation={useUploadFlexCredentialMutation}
					credentialByTypeQuery={useFlexCredentialByTypeQuery}
					onClose={() => {
						setShowUploadGenericModal(false);
						setCredentialTypeCode('');
					}}
					onUpload={(credentialType) => {
						setExpandedRows({ [credentialType]: true });
					}}
					isAttachmentRequired={false}
				/>
			)}

			{(showUploadPL || (!isFetching && hasNoProfessionalLicense)) && (
				<UploadPLCredentialModal
					caregiverId={caregiverId}
					managementCompanyId={managementCompanyId}
					primaryLicense={hasNoProfessionalLicense}
					uploadCredentialMutation={useUploadFlexCredentialMutation}
					credentialByTypeQuery={useFlexCredentialByTypeQuery}
					onClose={onClosePLUploadModal}
					onSuccess={onUploadSuccess}
				/>
			)}

			{showCredentialsByFacilityModal && (
				<ShowCredentialsByFacilityModal onClose={() => setShowCredentialsByFacilityModal(false)} />
			)}

			{showPrimaryLicenseModal && (
				<PrimaryLicenseModal
					caregiverId={caregiverId}
					onClose={onClosePrimaryLicenseModal}
					managementCompanyId={managementCompanyId}
					credentialByTypeQuery={useFlexCredentialByTypeQuery}
					setPrimaryLicenseGroupMutation={useSetFlexPrimaryLicenseGroupMutation}
				/>
			)}
		</div>
	);
};

export default FlexProfessionalsCredentialList;
